import axios from 'axios';

const USER_STORAGE_KEY = 'user';

export const getStoredUser = () => {
  const raw = localStorage.getItem(USER_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

export const getToken = () => {
  const user = getStoredUser();
  return user?.token || null;
};

export const clearSession = () => {
  localStorage.removeItem(USER_STORAGE_KEY);
};

// An employee whose JobDetails status is "Inactive" may still log in but only use Payslips and
// Tickets. The backend enforces this (InactiveEmployeeFilter) and answers 403 with this message;
// the status is also kept on the stored user so the router/sidebar can hide everything else.
export const EMPLOYEE_STATUS_CHANGED_EVENT = 'employee-status:changed';
const INACTIVE_ERROR_MESSAGE = 'Your account is inactive. Only payslips and tickets are available.';

export const isInactiveStatus = (status) => String(status || '').trim().toLowerCase() === 'inactive';

export const isStoredEmployeeInactive = () => {
  const user = getStoredUser();
  return user?.role === 'employee' && isInactiveStatus(user.employeeStatus);
};

// Updates the status on the stored session (no-op once logged out) and tells App.js/the layout.
export const setStoredEmployeeStatus = (employeeStatus) => {
  const user = getStoredUser();
  if (!user || (user.employeeStatus ?? null) === (employeeStatus ?? null)) return;
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify({ ...user, employeeStatus }));
  window.dispatchEvent(new CustomEvent(EMPLOYEE_STATUS_CHANGED_EVENT, { detail: employeeStatus }));
};

// A 403 carrying the inactive message means the admin deactivated this employee mid-session.
const handleInactiveError = (body) => {
  if (body?.error === INACTIVE_ERROR_MESSAGE) {
    setStoredEmployeeStatus('Inactive');
  }
};

let unauthorizedHandler = () => {
  window.location.href = '/login';
};

// Lets App.js swap in a handler that also resets in-memory auth state instead of
// relying on a hard page navigation.
export const setUnauthorizedHandler = (handler) => {
  unauthorizedHandler = handler;
};

const handleUnauthorized = () => {
  clearSession();
  unauthorizedHandler();
};

// authService/employeeService/holidayService all import the default axios instance, so
// registering the interceptor once here attaches it to every axios call in the app.
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      handleUnauthorized();
    } else if (error?.response?.status === 403) {
      handleInactiveError(error.response.data);
    }
    return Promise.reject(error);
  }
);

// Drop-in replacement for the native fetch() used by services that don't go through axios
// (leaveService, payrollService, attendanceService, and a few page components). Attaches the
// bearer token and reacts to session expiry the same way the axios interceptor does.
export const apiFetch = async (url, options = {}) => {
  const token = getToken();
  const headers = { ...(options.headers || {}) };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, { ...options, headers });
  if (response.status === 401) {
    handleUnauthorized();
  } else if (response.status === 403) {
    // Read a clone so the caller can still consume the original body.
    response.clone().json().then(handleInactiveError).catch(() => {});
  }
  return response;
};
